import type { IndexRouteObject, NonIndexRouteObject } from "react-router-dom";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { ActivitiesListPageResolver } from "web_myplann/Activities/Pages/ActivitiesListPageResolver";
import { ActivityDetailPageResolver } from "web_myplann/Activities/Pages/ActivityDetailPageResolver";
import { AdvisorPage } from "web_myplann/Advisor/Pages/AdvisorPage";
import { ChatListPageResolver } from "web_myplann/Chat/Pages/ChatListPageResolver";
import { ChatPageResolver } from "web_myplann/Chat/Pages/ChatPageResolver";
import { AllNotificationsTab } from "web_myplann/Dashboard/Pages/AllNotificationsTab";
import { DashboardDetailPageResolver } from "web_myplann/Dashboard/Pages/DashboardDetailPageResolver";
import { DashboardPageResolver } from "web_myplann/Dashboard/Pages/DashboardPageResolver";
import { NotificationsTabsLayout } from "web_myplann/Dashboard/Pages/NotificationsTabsLayout";
import { UnreadNotificationsTab } from "web_myplann/Dashboard/Pages/UnreadNotificationsTab";
import { ExpensesPage } from "web_myplann/Finances/Pages/ExpensesPage";
import { FinancesIntro } from "web_myplann/Finances/Pages/FinancesIntro";
import { FinancesOverview } from "web_myplann/Finances/Pages/FinancesOverview";
import { IncomePage } from "web_myplann/Finances/Pages/IncomePage";
import { GoalDetailPage } from "web_myplann/Goals/Pages/GoalDetailPage";
import { GoalFormPage } from "web_myplann/Goals/Pages/GoalFormPage";
import { GoalsListPage } from "web_myplann/Goals/Pages/GoalsListPage";
import { Error404Page } from "web_myplann/Infrastructure/Pages/Error404Page";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { InspirationDetailPage } from "web_myplann/Inspiration/Pages/InspirationDetailPage";
import { MorePage } from "web_myplann/More/Pages/MorePage";
import { SecurityPage } from "web_myplann/More/Pages/SecurityPage";
import { ToolHistory } from "web_myplann/Portfolio/Components/PortfolioTools/ToolHistory";
import { AddArtPage } from "web_myplann/Portfolio/Pages/ArtCreate/AddArtPage";
import { AddArtSuccessPage } from "web_myplann/Portfolio/Pages/ArtCreate/AddArtSuccessPage";
import { EditArtPage } from "web_myplann/Portfolio/Pages/ArtEdit/EditArtPage";
import { AddBankProductPage } from "web_myplann/Portfolio/Pages/BankProductCreate/AddBankProductPage";
import { AddBankProductSuccessPage } from "web_myplann/Portfolio/Pages/BankProductCreate/AddBankProductSuccessPage";
import { EditBankProductPage } from "web_myplann/Portfolio/Pages/BankProductEdit/EditBankProductPage";
import { AddBuildingSavingsPage } from "web_myplann/Portfolio/Pages/BuildingSavingsCreate/AddBuildingSavingsPage";
import { AddBuildingSavingsSuccessPage } from "web_myplann/Portfolio/Pages/BuildingSavingsCreate/AddBuildingSavingsSuccessPage";
import { EditBuildingSavingsPage } from "web_myplann/Portfolio/Pages/BuildingSavingsEdit/EditBuildingSavingsPage";
import { AddBusinessLoansPage } from "web_myplann/Portfolio/Pages/BusinessLoansCreate/AddBusinessLoansPage";
import { AddBusinessLoansSuccessPage } from "web_myplann/Portfolio/Pages/BusinessLoansCreate/AddBusinessLoansSuccessPage";
import { EditBusinessLoansPage } from "web_myplann/Portfolio/Pages/BusinessLoansEdit/EditBusinessLoansPage";
import { AddCarPage } from "web_myplann/Portfolio/Pages/CarCreate/AddCarPage";
import { AddCarSuccessPage } from "web_myplann/Portfolio/Pages/CarCreate/AddCarSuccessPage";
import { EditCarPage } from "web_myplann/Portfolio/Pages/CarEdit/EditCarPage";
import { AddCommoditiesPage } from "web_myplann/Portfolio/Pages/CommoditiesCreate/AddCommoditiesPage";
import { AddCommoditiesSuccessPage } from "web_myplann/Portfolio/Pages/CommoditiesCreate/AddCommoditiesSuccessPage";
import { EditCommoditiesPage } from "web_myplann/Portfolio/Pages/CommoditiesEdit/EditCommoditiesPage";
import { AddCryptocurrencyPage } from "web_myplann/Portfolio/Pages/CryptocurrencyCreate/AddCryptocurrencyPage";
import { AddCryptocurrencySuccessPage } from "web_myplann/Portfolio/Pages/CryptocurrencyCreate/AddCryptocurrencySuccessPage";
import { EditCryptocurrencyPage } from "web_myplann/Portfolio/Pages/CryptocurrencyEdit/EditCryptocurrencyPage";
import { FolderDetailPage } from "web_myplann/Portfolio/Pages/Documents/FolderDetailPage";
import { DocumentsListTab } from "web_myplann/Portfolio/Pages/DocumentsListTab";
import { AddInvestmentPage } from "web_myplann/Portfolio/Pages/InvestmentCreate/AddInvestmentPage";
import { AddInvestmentSuccessPage } from "web_myplann/Portfolio/Pages/InvestmentCreate/AddInvestmentSuccessPage";
import { EditInvestmentPage } from "web_myplann/Portfolio/Pages/InvestmentEdit/EditInvestmentPage";
import { AddLandSuccessPage } from "web_myplann/Portfolio/Pages/LandCreate/AddLandSuccessPage";
import { EditLandPage } from "web_myplann/Portfolio/Pages/LandEdit/EditLandPage";
import { AddLeasingPage } from "web_myplann/Portfolio/Pages/LeasingCreate/AddLeasingPage";
import { AddLeasingSuccessPage } from "web_myplann/Portfolio/Pages/LeasingCreate/AddLeasingSuccessPage";
import { EditLeasingPage } from "web_myplann/Portfolio/Pages/LeasingEdit/EditLeasingPage";
import { AddLifeInsurancePage } from "web_myplann/Portfolio/Pages/LifeInsuranceCreate/AddLifeInsurancePage";
import { AddLifeInsuranceSuccessPage } from "web_myplann/Portfolio/Pages/LifeInsuranceCreate/AddLifeInsuranceSuccessPage";
import { EditLifeInsurancePage } from "web_myplann/Portfolio/Pages/LifeInsuranceEdit/EditLifeInsurancePage";
import { AddPensionPage } from "web_myplann/Portfolio/Pages/PensionCreate/AddPensionPage";
import { AddPensionSuccessPage } from "web_myplann/Portfolio/Pages/PensionCreate/AddPensionSuccessPage";
import { EditPensionPage } from "web_myplann/Portfolio/Pages/PensionEdit/EditPensionPage";
import { PortfolioAddItemPage } from "web_myplann/Portfolio/Pages/PortfolioAddItemPage";
import { PortfolioDetailPage } from "web_myplann/Portfolio/Pages/PortfolioDetailPage";
import { PortfolioListTab } from "web_myplann/Portfolio/Pages/PortfolioListTab";
import { PortfolioTabsLayout } from "web_myplann/Portfolio/Pages/PortfolioTabsLayout";
import { PropertyDetailPage } from "web_myplann/Portfolio/Pages/PropertyDetailPage";
import { AddRealEstatePage } from "web_myplann/Portfolio/Pages/RealEstateCreate/AddRealEstatePage";
import { AddRealEstateSuccessPage } from "web_myplann/Portfolio/Pages/RealEstateCreate/AddRealEstateSuccessPage";
import { EditRealEstatePage } from "web_myplann/Portfolio/Pages/RealEstateEdit/EditRealEstatePage";
import { AddServicesPage } from "web_myplann/Portfolio/Pages/ServicesCreate/AddServicesPage";
import { AddServicesSuccessPage } from "web_myplann/Portfolio/Pages/ServicesCreate/AddServicesSuccessPage";
import { EditServicesPage } from "web_myplann/Portfolio/Pages/ServicesEdit/EditServicesPage";
import { AddressesPage } from "web_myplann/Profile/Pages/AddressesPage";
import { PaperworkPage } from "web_myplann/Profile/Pages/PaperworkPage";
import { PersonalDataAndAccountsPage } from "web_myplann/Profile/Pages/PersonalDataAndAccountsPage";
import { ProfilePageResolver } from "web_myplann/Profile/Pages/ProfilePageResolver";
import { SettingsPageResolver } from "web_myplann/Profile/Pages/SettingsPageResolver";
import { RouteNativeSync } from "web_myplann/Shared/Header/RouteNativeSync";
import type { RouteHandle } from "web_myplann/Shared/Hooks/useRouteHandleFlag";
import { MyPlannAuthenticatedRoute } from "web_myplann/Shared/MyPlannAuthenticatedRoute";
import { StandalonePortfolioListWrapper } from "web_myplann/Standalone/StandalonePortfolioListWrapper";
import { TaskDetailPageResolver } from "web_myplann/Tasks/Pages/TaskDetailPageResolver";

type AppRoute =
	| (Omit<IndexRouteObject, "handle"> & { handle?: RouteHandle })
	| (Omit<NonIndexRouteObject, "handle" | "children"> & {
			handle?: RouteHandle;
			children?: AppRoute[];
	  });

export const routes: AppRoute[] = [
	{
		path: getRoute(Routing.ActivitiesList),
		handle: { showNavbar: true },
		element: (
			<MyPlannAuthenticatedRoute>
				<ActivitiesListPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.ActivityDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<ActivityDetailPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.TaskDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<TaskDetailPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.StandAlone),
		element: (
			<MyPlannAuthenticatedRoute>
				<StandalonePortfolioListWrapper />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioTabs),
		handle: { showNavbar: true },
		element: (
			<MyPlannAuthenticatedRoute>
				<PortfolioTabsLayout />
			</MyPlannAuthenticatedRoute>
		),
		children: [
			{
				path: "portfolios",
				element: <PortfolioListTab />,
			},
			{
				path: "documents",
				element: <DocumentsListTab />,
			},
		],
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddItem),
		element: (
			<MyPlannAuthenticatedRoute>
				<PortfolioAddItemPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddCar),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddCarPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddCarSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddCarSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditCar),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditCarPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<PortfolioDetailPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PropertyDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<PropertyDetailPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddArt),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddArtPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddArtSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddArtSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditArt),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditArtPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddBuildingSavings),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddBuildingSavingsPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddBuildingSavingsSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddBuildingSavingsSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddPension),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddPensionPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddPensionSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddPensionSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditPension),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditPensionPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditBuildingSavings),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditBuildingSavingsPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddServices),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddServicesPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddServicesSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddServicesSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditServices),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditServicesPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddInvestment),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddInvestmentPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddInvestmentSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddInvestmentSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditInvestment),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditInvestmentPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddBankProduct),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddBankProductPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddBankProductSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddBankProductSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditBankProduct),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditBankProductPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddCommodities),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddCommoditiesPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddCommoditiesSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddCommoditiesSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditCommodities),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditCommoditiesPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddLifeInsurance),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddLifeInsurancePage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddLifeInsuranceSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddLifeInsuranceSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditLifeInsurance),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditLifeInsurancePage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddLeasing),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddLeasingPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddLeasingSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddLeasingSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditLeasing),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditLeasingPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddBusinessLoans),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddBusinessLoansPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddBusinessLoansSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddBusinessLoansSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditBusinessLoans),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditBusinessLoansPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddCryptocurrency),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddCryptocurrencyPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddCryptocurrencySuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddCryptocurrencySuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditCryptocurrency),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditCryptocurrencyPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddLandSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddLandSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditLand),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditLandPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddRealEstate),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddRealEstatePage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioAddRealEstateSuccess),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddRealEstateSuccessPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioEditRealEstate),
		element: (
			<MyPlannAuthenticatedRoute>
				<EditRealEstatePage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.ProfileDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<ProfilePageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.PersonalDataAndAccounts),
		element: (
			<MyPlannAuthenticatedRoute>
				<PersonalDataAndAccountsPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.Addresses),
		element: (
			<MyPlannAuthenticatedRoute>
				<AddressesPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.Paperwork),
		element: (
			<MyPlannAuthenticatedRoute>
				<PaperworkPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.More),
		element: (
			<MyPlannAuthenticatedRoute>
				<MorePage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.Security),
		element: (
			<MyPlannAuthenticatedRoute>
				<SecurityPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Profile.Settings),
		element: (
			<MyPlannAuthenticatedRoute>
				<SettingsPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.DashboardDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<DashboardDetailPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Finances.Intro),
		element: (
			<MyPlannAuthenticatedRoute>
				<FinancesIntro />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Finances.Income),
		element: (
			<MyPlannAuthenticatedRoute>
				<IncomePage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Finances.Expenses),
		element: (
			<MyPlannAuthenticatedRoute>
				<ExpensesPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Finances.Overview),
		element: (
			<MyPlannAuthenticatedRoute>
				<FinancesOverview />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.PortfolioDetailHistory),
		element: (
			<MyPlannAuthenticatedRoute>
				<ToolHistory />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Portfolio.FolderDetail),
		element: (
			<MyPlannAuthenticatedRoute>
				<FolderDetailPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Notifications),
		element: (
			<MyPlannAuthenticatedRoute>
				<NotificationsTabsLayout />
			</MyPlannAuthenticatedRoute>
		),
		children: [
			{
				path: "unread",
				element: <UnreadNotificationsTab />,
			},
			{
				path: "all",
				element: <AllNotificationsTab />,
			},
		],
	},
	{
		path: getRoute(Routing.Advisor),
		handle: { showNavbar: true },
		element: (
			<MyPlannAuthenticatedRoute>
				<AdvisorPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Chat.List),
		element: (
			<MyPlannAuthenticatedRoute>
				<ChatListPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: Routing.Chat.Detail.route,
		element: (
			<MyPlannAuthenticatedRoute>
				<ChatPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Goals.GoalsList),
		handle: { showNavbar: true },
		element: (
			<MyPlannAuthenticatedRoute>
				<GoalsListPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Goals.GoalFormCreate),
		element: (
			<MyPlannAuthenticatedRoute>
				<GoalFormPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: getRoute(Routing.Goals.GoalFormEdit),
		element: (
			<MyPlannAuthenticatedRoute>
				<GoalFormPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: Routing.Goals.GoalDetail.route,
		element: (
			<MyPlannAuthenticatedRoute>
				<GoalDetailPage />
			</MyPlannAuthenticatedRoute>
		),
	},
	{
		path: Routing.Inspiration.Detail.route,
		handle: { statusBarOverlay: true },
		element: (
			<MyPlannAuthenticatedRoute>
				<InspirationDetailPage />
			</MyPlannAuthenticatedRoute>
		),
	},
];

const baseRoutes: AppRoute[] = [
	{
		index: true,
		element: <Navigate to={getRoute(Routing.Dashboard)} replace />,
	},
	{
		path: Routing.Dashboard.path(),
		handle: { showNavbar: true },
		element: (
			<MyPlannAuthenticatedRoute>
				<DashboardPageResolver />
			</MyPlannAuthenticatedRoute>
		),
	},
];

export const getRouter = () =>
	createBrowserRouter(
		[
			{
				element: <RouteNativeSync />,
				children: [
					...baseRoutes,
					...routes,
					{ path: "*", element: <Error404Page /> },
				],
			},
		],
		{
			basename: import.meta.env.VITE_BASE_URL?.replace(/\/$/, ""),
		},
	);
